import api from '@/lib/axios';
import type { Paginated } from '@/services/school.api';
import {
  NotificationRow,
  NotificationSectionOption,
  NotificationSendResult,
  NotificationType,
  NotificationUserOption,
} from '@/services/school.api';

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body: any = r.data;
  return (body?.data ?? body) as T;
}

export interface UploadResult {
  url: string;
  name: string;
  size: number;
  mime: string;
}

export interface CreateOrgNotificationData {
  schoolId: string;
  title: string;
  message: string;
  type: NotificationType;
  userIds?: string[];
  roles?: string[];
  sectionIds?: string[];
  imageUrl?: string;
}

export interface BroadcastOrgNotificationData {
  schoolIds: string[];
  title: string;
  message: string;
  imageUrl?: string;
}

export interface BroadcastResult {
  schools: { schoolId: string; schoolName: string; recipientCount: number; reachableCount: number }[];
  recipientCount: number;
  reachableCount: number;
}

export interface OrgNotificationRow extends NotificationRow {
  schoolId?: string;
  schoolName?: string;
}

export const OrgNotificationsApi = {
  schools: async (): Promise<{ id: string; name: string }[]> => unwrap(await api.get('/org/notifications/schools')),

  upload: async (schoolId: string, file: File): Promise<UploadResult> => {
    const fd = new FormData();
    fd.append('file', file);
    return unwrap(await api.post('/org/notifications/uploads', fd, { params: { schoolId } }));
  },

  /** Omit schoolId for every school in scope, flat — matches real legacy's own (session-free) history view. */
  list: async (schoolId?: string, params?: { page?: number; limit?: number }): Promise<Paginated<OrgNotificationRow>> =>
    unwrap(await api.get('/org/notifications', { params: { ...(schoolId ? { schoolId } : {}), ...params } })),

  create: async (data: CreateOrgNotificationData): Promise<NotificationSendResult> => unwrap(await api.post('/org/notifications', data)),

  createBroadcast: async (data: BroadcastOrgNotificationData): Promise<BroadcastResult> =>
    unwrap(await api.post('/org/notifications/broadcast', data)),

  remove: async (id: string, schoolId: string) => unwrap(await api.delete(`/org/notifications/${id}`, { params: { schoolId } })),

  lookupUsers: async (schoolId: string): Promise<NotificationUserOption[]> =>
    unwrap(await api.get('/org/notifications/lookup/users', { params: { schoolId } })),

  lookupSections: async (schoolId: string): Promise<NotificationSectionOption[]> =>
    unwrap(await api.get('/org/notifications/lookup/sections', { params: { schoolId } })),
};
